import type { SceneDef } from "../../genesis/schema";

/**
 * Les Voyages Missionnaires de Paul — Scène 3 : Éphèse, la Parole plus
 * forte que la magie. Ancrage biblique : Actes 19. École de Tyrannus où
 * Paul enseigne chaque jour pendant deux ans (Ac 19:9-10) ; ceux qui
 * avaient pratiqué la magie apportent leurs livres et les brûlent devant
 * tous — cinquante mille pièces d'argent (drag_drop → bûcher, Ac 19:19) ;
 * l'émeute du théâtre à traverser (« Grande est la Diane des Éphésiens ! »,
 * Ac 19:28-34) en conduisant Gaïus et Aristarque en sûreté (guide_target,
 * Ac 19:29) tandis que le greffier apaise la foule (lever_action,
 * Ac 19:35-41).
 * Interactables « avançants » (pickup + drag_drop + lever_action +
 * guide_target) : 21
 *   = 8 pickups (5 fragments du verset Ac 19:20, 3 pièces d'argent du
 *     compte des cinquante mille)
 *   + 8 drag_drop (5 livres de magie → bûcher, 3 rouleaux des Écritures
 *     → pupitre de Tyrannus)
 *   + 3 lever_action (le greffier apaise la foule, l'enseignement
 *     quotidien, la confession publique)
 *   + 2 guide_target (Gaïus et Aristarque vers la sortie du théâtre).
 * La difficulté D/A/E scale goalCount sur ce total réel (7 / 14 / 21).
 */
export const voyagesDePaulScene3: SceneDef = {
  id: "voyages-de-paul/scene-3",
  game: "voyages-de-paul",
  title: {
    fr: "Les Voyages Missionnaires de Paul — Éphèse, la Parole plus forte que la magie",
    en: "Paul's Missionary Journeys — Ephesus, the Word Stronger than Magic",
  },
  order: 3,
  keyName: { fr: "Clé de la Délivrance", en: "Key of Deliverance" },
  verseBrand: "Matthieu 16:19",
  env: {
    groundSize: 64,
    skyColor: "#d99a5b",
    groundColor: "#7a6f5c",
    lighting: "dusk",
    props: [
      // Les gradins du grand théâtre d'Éphèse, au nord (25 000 places)
      { id: "gradins_theatre", shape: "box", pos: [0, 0, -27], color: "#a3947c", size: 2.6, scatter: 14, scatterRadius: 16 },
      // La foule en émeute dans le théâtre (Ac 19:29,32)
      { id: "foule_emeute", shape: "person", pos: [0, 0, -22], color: "#8a5a48", size: 1.3, scatter: 14, scatterRadius: 10 },
      // Colonnes du temple d'Artémis, merveille du monde, au levant
      { id: "temple_artemis", shape: "cylinder", pos: [27, 0, -12], color: "#d8cdb2", size: 3, scatter: 10, scatterRadius: 11 },
      // Braises et étincelles autour de la place du bûcher
      { id: "braises_bucher", shape: "flame", pos: [0, 0, -6], color: "#e8933a", size: 0.5, scatter: 10, scatterRadius: 6 },
      // Toits de la ville d'Éphèse au crépuscule
      { id: "toits_ephese", shape: "box", pos: [-26, 0, 16], color: "#5d5244", size: 2, scatter: 12, scatterRadius: 12 },
      // Le portique de l'école de Tyrannus, à l'ouest
      { id: "portique_ecole", shape: "cylinder", pos: [-20, 0, 0], color: "#b8ac90", size: 2.2, scatter: 8, scatterRadius: 9 },
      // La voie Arcadiane descendant vers le port
      { id: "voie_arcadiane", shape: "box", pos: [14, 0, 20], color: "#c2bba8", size: 0.9, scatter: 12, scatterRadius: 14 },
      // Le port d'Éphèse et la mer Égée au couchant
      { id: "port_egee", shape: "water", pos: [26, 0, 24], color: "#3e5f7a", size: 3, scatter: 10, scatterRadius: 9 },
      // Palmiers et platanes le long des rues
      { id: "palmiers", shape: "tree", pos: [-8, 0, 22], color: "#4f6b3f", size: 1.9, scatter: 10, scatterRadius: 12 },
      // Lampes du soir qui s'allument sur les échoppes
      { id: "lampes_soir", shape: "flame", pos: [16, 0, 6], color: "#e0a44a", size: 0.5, scatter: 10, scatterRadius: 14 },
      // Collines du mont Pion derrière le théâtre
      { id: "mont_pion", shape: "mountain", pos: [-14, 0, -28], color: "#6b5f4e", size: 6, scatter: 4, scatterRadius: 10 },
      // Étals des orfèvres, sanctuaires d'argent de Démétrius (Ac 19:24)
      { id: "etals_orfevres", shape: "box", pos: [18, 0, -6], color: "#8c8c94", size: 1, scatter: 8, scatterRadius: 7 },
    ],
  },
  spawn: [0, 0, 14],
  difficulty: {
    // goalCount = interactables avançants accomplis (21 au total :
    // 8 pickups + 8 drag_drop + 3 lever_action + 2 guide_target).
    D: { goalCount: 7, hints: true },
    A: { goalCount: 14, hints: true },
    E: { goalCount: 21, hints: false, timerS: 480 },
  },
  interactables: [
    // ——— Le greffier apaise l'émeute : mécanique signature ———
    {
      id: "greffier_calme",
      type: "lever_action",
      label: { fr: "Le greffier de la ville — maintiens pour apaiser la foule du théâtre (Ac 19:35)", en: "The town clerk — hold to quiet the theater crowd (Acts 19:35)" },
      pos: [0, 0, -26],
      shape: "person",
      color: "#e8c14a",
      size: 1.5,
      holdS: 4,
      count: 1,
    },
    {
      id: "enseignement_quotidien",
      type: "lever_action",
      label: { fr: "L'école de Tyrannus — tiens l'enseignement quotidien, deux ans durant (Ac 19:9-10)", en: "The school of Tyrannus — hold the daily teaching, for two years (Acts 19:9-10)" },
      pos: [-21, 0, 4],
      shape: "stele",
      color: "#c9b98c",
      size: 1.3,
      holdS: 3,
      count: 1,
    },
    {
      id: "confession_publique",
      type: "lever_action",
      label: { fr: "La place de l'aveu — plusieurs venaient confesser et déclarer ce qu'ils avaient fait (Ac 19:18)", en: "The place of confession — many came confessing and declaring their deeds (Acts 19:18)" },
      pos: [4, 0, -2],
      shape: "person",
      color: "#d4b054",
      size: 1.3,
      holdS: 3,
      count: 1,
    },
    // ——— Personnages nommés (npc_dialog) ———
    {
      id: "paul_ephese",
      type: "npc_dialog",
      label: { fr: "Paul : « La parole du Seigneur croissait en puissance et en force » (Ac 19:20)", en: "Paul: \"The word of the Lord was growing and becoming mighty\" (Acts 19:20)" },
      pos: [-16, 0, 2],
      shape: "person",
      color: "#e8c14a",
      size: 1.4,
    },
    {
      id: "tyrannus",
      type: "npc_dialog",
      label: { fr: "Tyrannus — il ouvrit son école pour que la Parole soit annoncée chaque jour (Ac 19:9)", en: "Tyrannus — he opened his school so the Word could be taught daily (Acts 19:9)" },
      pos: [-19, 0, -2],
      shape: "person",
      color: "#8a9bb5",
      size: 1.35,
    },
    {
      id: "priscille",
      type: "npc_dialog",
      label: { fr: "Priscille — avec Aquilas, elle exposa plus exactement la voie de Dieu (Ac 18:26)", en: "Priscilla — with Aquila, she explained the way of God more accurately (Acts 18:26)" },
      pos: [-13, 0, 7],
      shape: "person",
      color: "#a87a9b",
      size: 1.35,
    },
    {
      id: "aquilas",
      type: "npc_dialog",
      label: { fr: "Aquilas, faiseur de tentes — compagnon d'œuvre de Paul à Éphèse (Ac 18:18-19,26)", en: "Aquila, tentmaker — Paul's fellow worker in Ephesus (Acts 18:18-19,26)" },
      pos: [-16, 0, 9],
      shape: "person",
      color: "#6f8a6b",
      size: 1.35,
    },
    {
      id: "demetrius",
      type: "npc_dialog",
      label: { fr: "Démétrius l'orfèvre : « Notre gain est menacé ! » — écoute la source de l'émeute (Ac 19:24-27)", en: "Demetrius the silversmith: \"Our trade is in danger!\" — hear the source of the riot (Acts 19:24-27)" },
      pos: [12, 0, -10],
      shape: "person",
      color: "#b5824a",
      size: 1.4,
    },
    // ——— Le bûcher public et le pupitre de l'école (build_slot) ———
    {
      id: "bucher",
      type: "build_slot",
      label: { fr: "Le bûcher public — ici les livres de magie furent brûlés devant tous (Ac 19:19)", en: "The public bonfire — here the magic books were burned in the sight of all (Acts 19:19)" },
      pos: [0, 0, -8],
      shape: "flame",
      color: "#e07f2e",
      size: 2,
      targetId: "bucher",
    },
    {
      id: "pupitre_tyrannus",
      type: "build_slot",
      label: { fr: "Le pupitre de l'école de Tyrannus — dresse-le pour l'enseignement (Ac 19:9)", en: "The lectern of the school of Tyrannus — set it up for the teaching (Acts 19:9)" },
      pos: [-18, 0, -6],
      shape: "box",
      color: "#7a5a38",
      size: 1.3,
      targetId: "pupitre_tyrannus",
    },
    // ——— Les livres de magie : au feu, devant tous (drag_drop) ———
    {
      id: "livre_sorts",
      type: "drag_drop",
      label: { fr: "Livre de sortilèges — porte-le au bûcher, devant tout le monde (Ac 19:19)", en: "Book of spells — carry it to the bonfire, in the sight of all (Acts 19:19)" },
      pos: [8, 0, 6],
      shape: "box",
      color: "#5c4a6e",
      size: 0.6,
      targetId: "bucher",
      count: 1,
    },
    {
      id: "livre_incantations",
      type: "drag_drop",
      label: { fr: "Livre d'incantations — renonce-y et porte-le au feu (Ac 19:18-19)", en: "Book of incantations — renounce it and carry it to the fire (Acts 19:18-19)" },
      pos: [-8, 0, 6],
      shape: "box",
      color: "#5c4a6e",
      size: 0.6,
      targetId: "bucher",
      count: 1,
    },
    {
      id: "livre_amulettes",
      type: "drag_drop",
      label: { fr: "Registre d'amulettes — au bûcher : la lumière vaut mieux que les secrets", en: "Register of amulets — to the bonfire: light is worth more than secrets" },
      pos: [10, 0, -2],
      shape: "box",
      color: "#4e3f60",
      size: 0.6,
      targetId: "bucher",
      count: 1,
    },
    {
      id: "livre_presages",
      type: "drag_drop",
      label: { fr: "Livre de présages — au feu, car la Parole du Seigneur suffit (Ac 19:20)", en: "Book of omens — to the fire, for the Word of the Lord is enough (Acts 19:20)" },
      pos: [-6, 0, -12],
      shape: "box",
      color: "#4e3f60",
      size: 0.6,
      targetId: "bucher",
      count: 1,
    },
    {
      id: "livre_ephesia",
      type: "drag_drop",
      label: { fr: "Rouleau des « lettres éphésiennes » — les fameuses formules magiques, au bûcher", en: "Scroll of the 'Ephesian letters' — the famous magic formulas, to the bonfire" },
      pos: [6, 0, -14],
      shape: "box",
      color: "#5c4a6e",
      size: 0.6,
      targetId: "bucher",
      count: 1,
    },
    // ——— Rouleaux des Écritures → pupitre de l'école (drag_drop) ———
    {
      id: "rouleau_evangile",
      type: "drag_drop",
      label: { fr: "Rouleau de l'Évangile — porte-le au pupitre de Tyrannus (Ac 19:10)", en: "Gospel scroll — carry it to Tyrannus' lectern (Acts 19:10)" },
      pos: [-24, 0, 8],
      shape: "cylinder",
      color: "#cdb98a",
      size: 0.6,
      targetId: "pupitre_tyrannus",
      count: 1,
    },
    {
      id: "rouleau_loi",
      type: "drag_drop",
      label: { fr: "Rouleau de la Loi — pour l'enseignement quotidien de l'école", en: "Scroll of the Law — for the school's daily teaching" },
      pos: [-26, 0, 0],
      shape: "cylinder",
      color: "#cdb98a",
      size: 0.6,
      targetId: "pupitre_tyrannus",
      count: 1,
    },
    {
      id: "rouleau_prophetes_eph",
      type: "drag_drop",
      label: { fr: "Rouleau des Prophètes — toute l'Asie entendit la parole du Seigneur (Ac 19:10)", en: "Scroll of the Prophets — all Asia heard the word of the Lord (Acts 19:10)" },
      pos: [-24, 0, -10],
      shape: "cylinder",
      color: "#c2ad7c",
      size: 0.6,
      targetId: "pupitre_tyrannus",
      count: 1,
    },
    // ——— Fragments du verset Actes 19:20 (pickup) ———
    {
      id: "frag_ainsi",
      type: "pickup",
      label: { fr: "Fragment du verset : « C'est ainsi que » (Ac 19:20)", en: "Verse fragment: \"So\" (Acts 19:20)" },
      pos: [16, 0, 8],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "frag_parole",
      type: "pickup",
      label: { fr: "Fragment du verset : « la parole du Seigneur » (Ac 19:20)", en: "Verse fragment: \"the word of the Lord\" (Acts 19:20)" },
      pos: [20, 0, 3],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "frag_croissait",
      type: "pickup",
      label: { fr: "Fragment du verset : « croissait » (Ac 19:20)", en: "Verse fragment: \"was growing\" (Acts 19:20)" },
      pos: [24, 0, 10],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "frag_puissance",
      type: "pickup",
      label: { fr: "Fragment du verset : « en puissance » (Ac 19:20)", en: "Verse fragment: \"in power\" (Acts 19:20)" },
      pos: [19, 0, 14],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    {
      id: "frag_force",
      type: "pickup",
      label: { fr: "Fragment du verset : « et en force » (Ac 19:20)", en: "Verse fragment: \"and becoming mighty\" (Acts 19:20)" },
      pos: [24, 0, -2],
      shape: "sphere",
      color: "#f0e3b0",
      size: 0.5,
    },
    // ——— Les pièces d'argent du compte (Ac 19:19) ———
    {
      id: "piece_argent_1",
      type: "pickup",
      label: { fr: "Pièce d'argent — on estima la valeur des livres à cinquante mille pièces (Ac 19:19)", en: "Silver coin — the books' value was counted at fifty thousand pieces (Acts 19:19)" },
      pos: [2, 0, 4],
      shape: "sphere",
      color: "#c9ccd4",
      size: 0.4,
    },
    {
      id: "piece_argent_2",
      type: "pickup",
      label: { fr: "Seconde pièce du compte — un grand prix, librement abandonné", en: "Second coin of the count — a great price, freely given up" },
      pos: [-3, 0, 1],
      shape: "sphere",
      color: "#c9ccd4",
      size: 0.4,
    },
    {
      id: "piece_argent_3",
      type: "pickup",
      label: { fr: "Troisième pièce du compte — la liberté vaut plus que l'argent", en: "Third coin of the count — freedom is worth more than silver" },
      pos: [13, 0, 2],
      shape: "sphere",
      color: "#c9ccd4",
      size: 0.4,
    },
    // ——— L'émeute du théâtre : conduire les compagnons en sûreté ———
    {
      id: "gaius",
      type: "guide_target",
      label: { fr: "Gaïus, entraîné dans le théâtre — traverse la foule et conduis-le vers la sortie (Ac 19:29)", en: "Gaius, dragged into the theater — cross the crowd and lead him to the exit (Acts 19:29)" },
      pos: [8, 0, -22],
      shape: "person",
      color: "#c4a06a",
      size: 1.2,
      targetId: "sortie_theatre",
      count: 1,
    },
    {
      id: "aristarque",
      type: "guide_target",
      label: { fr: "Aristarque de Macédoine — conduis-le hors du tumulte, sans un coup rendu (Ac 19:29)", en: "Aristarchus of Macedonia — lead him out of the uproar, without returning a blow (Acts 19:29)" },
      pos: [-8, 0, -22],
      shape: "person",
      color: "#c4a06a",
      size: 1.2,
      targetId: "sortie_theatre",
      count: 1,
    },
    // ——— Panneaux bibliques ———
    {
      id: "panel_tyrannus",
      type: "panel",
      label: { fr: "Lire : deux ans à l'école de Tyrannus", en: "Read: two years in the school of Tyrannus" },
      pos: [-12, 0, -2],
      shape: "stele",
      color: "#b9a06a",
      size: 1.4,
      panelId: "p_ac19_9",
    },
    {
      id: "sortie_theatre",
      type: "panel",
      label: { fr: "La porte sud du théâtre — l'assemblée congédiée, chacun rentre en paix (Ac 19:41)", en: "The theater's south gate — the assembly dismissed, everyone goes home in peace (Acts 19:41)" },
      pos: [0, 0, -18],
      shape: "gate",
      color: "#8c8578",
      size: 2.4,
      panelId: "p_ac19_20",
    },
  ],
  objectives: [
    {
      id: "obj_bucher",
      kind: "build",
      label: { fr: "Apporte les cinq livres de magie au bûcher — brûlés devant tous (Ac 19:19)", en: "Bring the five magic books to the bonfire — burned in the sight of all (Acts 19:19)" },
      target: "bucher",
      count: 5,
    },
    {
      id: "obj_traversee",
      kind: "guide",
      label: { fr: "Traverse l'émeute du théâtre et conduis Gaïus et Aristarque en sûreté (Ac 19:29)", en: "Cross the theater riot and lead Gaius and Aristarchus to safety (Acts 19:29)" },
      target: "sortie_theatre",
      count: 2,
    },
  ],
  panels: [
    {
      id: "p_ac19_9",
      title: { fr: "Chaque jour, à l'école de Tyrannus", en: "Daily, in the school of Tyrannus" },
      reference: "Actes 19:9-10",
      passage: {
        fr: "Mais, comme quelques-uns restaient endurcis et incrédules, décriant devant la multitude la voie du Seigneur, il se retira d'eux, sépara les disciples, et enseigna chaque jour dans l'école d'un nommé Tyrannus. Cela dura deux ans, de sorte que tous ceux qui habitaient l'Asie, Juifs et Grecs, entendirent la parole du Seigneur.",
        en: "But when some were hardened and disobedient, speaking evil of the Way before the multitude, he departed from them, and separated the disciples, reasoning daily in the school of Tyrannus. This continued for two years, so that all those who lived in Asia heard the word of the Lord Jesus, both Jews and Greeks.",
      },
      explain: {
        fr: "Pas de coup d'éclat ici : une salle de classe, et deux ans de fidélité quotidienne. C'est par cette persévérance discrète que « tous ceux qui habitaient l'Asie » entendirent la Parole. Les grandes moissons naissent souvent d'un enseignement répété, jour après jour, au même endroit.",
        en: "No dramatic stroke here: a classroom, and two years of daily faithfulness. It was through that quiet perseverance that 'all those who lived in Asia' heard the Word. Great harvests are often born of teaching repeated day after day, in the same place.",
      },
      didYouKnow: {
        fr: "Éphèse comptait parmi les plus grandes villes de l'Empire, avec son théâtre d'environ 25 000 places (celui de l'émeute d'Actes 19) et le temple d'Artémis, l'une des sept merveilles du monde antique.",
        en: "Ephesus was among the largest cities of the Empire, with its theater of about 25,000 seats (the one of the riot in Acts 19) and the temple of Artemis, one of the seven wonders of the ancient world.",
      },
      application: {
        fr: "Quelle fidélité « quotidienne » — petite mais répétée — Dieu te confie-t-il en ce moment ?",
        en: "What 'daily' faithfulness — small but repeated — is God entrusting to you right now?",
      },
    },
    {
      id: "p_ac19_20",
      title: { fr: "Les livres brûlés, la Parole qui croît", en: "The books burned, the Word growing" },
      reference: "Actes 19:19-20",
      passage: {
        fr: "Et un certain nombre de ceux qui avaient exercé les arts magiques, ayant apporté leurs livres, les brûlèrent devant tout le monde: on en estima la valeur à cinquante mille pièces d'argent. C'est ainsi que la parole du Seigneur croissait en puissance et en force.",
        en: "Many of those who practiced magical arts brought their books together and burned them in the sight of all. They counted their price, and found it to be fifty thousand pieces of silver. So the word of the Lord grew and became mighty.",
      },
      explain: {
        fr: "Personne ne leur arracha ces livres : ils les apportèrent eux-mêmes et les brûlèrent devant tous. Cinquante mille pièces d'argent partirent en fumée — mais des cœurs devinrent libres. Quand la Parole grandit dans une vie, ce qui asservissait perd son prix, et le renoncement devient une fête publique.",
        en: "No one snatched those books from them: they brought them themselves and burned them in the sight of all. Fifty thousand pieces of silver went up in smoke — but hearts became free. When the Word grows in a life, whatever enslaved loses its price, and renunciation becomes a public celebration.",
      },
      didYouKnow: {
        fr: "Une pièce d'argent représentait environ une journée de salaire : cinquante mille pièces équivalaient à des dizaines d'années de travail. Éphèse était célèbre pour ses formules magiques, dites « lettres éphésiennes ».",
        en: "One piece of silver was about a day's wage: fifty thousand pieces amounted to decades of labor. Ephesus was famous for its magic formulas, known as the 'Ephesian letters'.",
      },
      application: {
        fr: "Y a-t-il un « livre » — une habitude, une peur, un secret — que tu peux déposer au feu pour marcher libre ?",
        en: "Is there a 'book' — a habit, a fear, a secret — you can lay on the fire so as to walk free?",
      },
    },
  ],
  quizzes: [
    {
      id: "q_livres",
      level: "D",
      prompt: { fr: "Qu'ont fait ceux qui avaient pratiqué la magie après avoir cru ?", en: "What did those who had practiced magic do after believing?" },
      choices: [
        { fr: "Ils ont vendu leurs livres au marché", en: "They sold their books at the market" },
        { fr: "Ils ont apporté leurs livres et les ont brûlés devant tous", en: "They brought their books and burned them in the sight of all" },
        { fr: "Ils ont caché leurs livres dans le temple", en: "They hid their books in the temple" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Ayant apporté leurs livres, ils les brûlèrent devant tout le monde. » (Actes 19:19) Un renoncement libre et public : la liberté en Christ valait plus que cinquante mille pièces d'argent.",
        en: "\"They brought their books together and burned them in the sight of all.\" (Acts 19:19) A free and public renunciation: freedom in Christ was worth more than fifty thousand pieces of silver.",
      },
    },
    {
      id: "q_tyrannus",
      level: "A",
      prompt: { fr: "Où Paul enseigna-t-il chaque jour pendant deux ans à Éphèse ?", en: "Where did Paul teach daily for two years in Ephesus?" },
      choices: [
        { fr: "Dans le théâtre de la ville", en: "In the city theater" },
        { fr: "Sur le parvis du temple d'Artémis", en: "On the forecourt of the temple of Artemis" },
        { fr: "Dans l'école d'un nommé Tyrannus", en: "In the school of a man named Tyrannus" },
        { fr: "Sur le port, au bord de la mer", en: "At the harbor, by the sea" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Il enseigna chaque jour dans l'école d'un nommé Tyrannus. Cela dura deux ans, de sorte que tous ceux qui habitaient l'Asie… entendirent la parole du Seigneur. » (Actes 19:9-10)",
        en: "\"...reasoning daily in the school of Tyrannus. This continued for two years, so that all those who lived in Asia heard the word of the Lord.\" (Acts 19:9-10)",
      },
    },
    {
      id: "q_demetrius",
      level: "E",
      prompt: { fr: "Pourquoi Démétrius l'orfèvre souleva-t-il l'émeute du théâtre ?", en: "Why did Demetrius the silversmith stir up the theater riot?" },
      choices: [
        { fr: "Paul avait insulté publiquement la déesse", en: "Paul had publicly insulted the goddess" },
        { fr: "Son gain était menacé : plus personne n'achetait ses sanctuaires d'argent", en: "His trade was in danger: people no longer bought his silver shrines" },
        { fr: "Le gouverneur avait interdit les assemblées", en: "The governor had banned assemblies" },
        { fr: "Les disciples avaient occupé le théâtre", en: "The disciples had occupied the theater" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Démétrius fabriquait des temples de Diane en argent et « procurait aux artisans un gain considérable » (Ac 19:24). Voyant tant de gens se détourner des idoles, il craignit pour son commerce et ameuta la ville (Ac 19:25-29). Le greffier nota d'ailleurs que Paul n'avait ni pillé le temple ni blasphémé la déesse (Ac 19:37) : la Parole avait simplement changé les cœurs.",
        en: "Demetrius made silver shrines of Diana and \"brought no little business to the craftsmen\" (Acts 19:24). Seeing so many turn from idols, he feared for his trade and stirred up the city (Acts 19:25-29). The town clerk even noted that Paul had neither robbed the temple nor blasphemed the goddess (Acts 19:37): the Word had simply changed hearts.",
      },
    },
    {
      id: "qD1",
      level: "D",
      prompt: { fr: "À combien fut estimée la valeur des livres de magie brûlés ?", en: "At how much was the value of the burned magic books counted?" },
      choices: [
        { fr: "Cinq cents pièces d'argent", en: "Five hundred pieces of silver" },
        { fr: "Cinq mille pièces d'or", en: "Five thousand pieces of gold" },
        { fr: "Cinquante mille pièces d'argent", en: "Fifty thousand pieces of silver" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« On en estima la valeur à cinquante mille pièces d'argent. » (Actes 19:19) Une pièce représentait environ une journée de salaire : des dizaines d'années de travail partirent en fumée — mais des cœurs devinrent libres.",
        en: "\"They counted their price, and found it to be fifty thousand pieces of silver.\" (Acts 19:19) One piece was about a day's wage: decades of labor went up in smoke — but hearts became free.",
      },
    },
    {
      id: "qD2",
      level: "D",
      prompt: { fr: "Que criait la foule rassemblée dans le théâtre d'Éphèse ?", en: "What did the crowd gathered in the theater of Ephesus shout?" },
      choices: [
        { fr: "« Grande est la Diane des Éphésiens ! »", en: "\"Great is Diana of the Ephesians!\"" },
        { fr: "« Vive l'empereur de Rome ! »", en: "\"Long live the emperor of Rome!\"" },
        { fr: "« Fermez l'école de Tyrannus ! »", en: "\"Close the school of Tyrannus!\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Ils étaient tous pleins de colère, et ils s'écriaient: Grande est la Diane des Éphésiens ! » (Actes 19:28) Un cri de foule répété — mais le greffier montra qu'aucun tort réel n'avait été commis (Ac 19:37).",
        en: "\"They were filled with anger, and cried out, saying, 'Great is Diana of the Ephesians!'\" (Acts 19:28) A repeated crowd chant — yet the town clerk showed that no real wrong had been done (Acts 19:37).",
      },
    },
    {
      id: "qD3",
      level: "D",
      prompt: { fr: "Qui réussit finalement à apaiser la foule du théâtre ?", en: "Who finally managed to quiet the theater crowd?" },
      choices: [
        { fr: "Paul lui-même", en: "Paul himself" },
        { fr: "Le greffier de la ville", en: "The town clerk" },
        { fr: "Démétrius l'orfèvre", en: "Demetrius the silversmith" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Cependant le greffier, ayant apaisé la foule… » (Actes 19:35) Après son discours de sagesse, « il congédia l'assemblée » (Ac 19:41). Dieu peut se servir même d'un fonctionnaire païen pour protéger son Église.",
        en: "\"When the town clerk had quieted the multitude…\" (Acts 19:35) After his wise speech, \"he dismissed the assembly\" (Acts 19:41). God can use even a pagan official to protect his Church.",
      },
    },
    {
      id: "qD4",
      level: "D",
      prompt: { fr: "Quels compagnons de Paul furent entraînés dans le théâtre par la foule ?", en: "Which companions of Paul were dragged into the theater by the crowd?" },
      choices: [
        { fr: "Timothée et Tite", en: "Timothy and Titus" },
        { fr: "Priscille et Aquilas", en: "Priscilla and Aquila" },
        { fr: "Gaïus et Aristarque", en: "Gaius and Aristarchus" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Ils se précipitèrent tous ensemble au théâtre, entraînant avec eux Gaïus et Aristarque, Macédoniens, compagnons de voyage de Paul. » (Actes 19:29) Paul voulut les rejoindre, mais les disciples l'en empêchèrent (Ac 19:30).",
        en: "\"They rushed with one accord into the theater, having seized Gaius and Aristarchus, men of Macedonia, Paul's companions in travel.\" (Acts 19:29) Paul wanted to go in to them, but the disciples would not allow him (Acts 19:30).",
      },
    },
    {
      id: "qA1",
      level: "A",
      prompt: { fr: "Quel fut le fruit des deux années d'enseignement quotidien à l'école de Tyrannus ?", en: "What was the fruit of the two years of daily teaching in the school of Tyrannus?" },
      choices: [
        { fr: "Tous ceux qui habitaient l'Asie, Juifs et Grecs, entendirent la parole du Seigneur", en: "All those who lived in Asia, both Jews and Greeks, heard the word of the Lord" },
        { fr: "Le temple d'Artémis ferma ses portes", en: "The temple of Artemis closed its doors" },
        { fr: "Paul fut nommé magistrat de la ville", en: "Paul was appointed a magistrate of the city" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Cela dura deux ans, de sorte que tous ceux qui habitaient l'Asie, Juifs et Grecs, entendirent la parole du Seigneur. » (Actes 19:10) Une salle de classe et une fidélité quotidienne : c'est ainsi que toute une province fut atteinte.",
        en: "\"This continued for two years, so that all those who lived in Asia heard the word of the Lord Jesus, both Jews and Greeks.\" (Acts 19:10) A classroom and daily faithfulness: that is how a whole province was reached.",
      },
    },
    {
      id: "qA2",
      level: "A",
      prompt: { fr: "Que firent beaucoup de ceux qui avaient cru, avant même le bûcher des livres ?", en: "What did many of those who believed do, even before the burning of the books?" },
      choices: [
        { fr: "Ils vendirent leurs biens au profit du temple", en: "They sold their goods for the temple's benefit" },
        { fr: "Ils venaient confesser et déclarer publiquement ce qu'ils avaient fait", en: "They came confessing and declaring their deeds publicly" },
        { fr: "Ils quittèrent la ville en secret", en: "They left the city in secret" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Plusieurs de ceux qui avaient cru venaient confesser et déclarer ce qu'ils avaient fait. » (Actes 19:18) La lumière commence par la vérité sur soi-même ; la confession précède et prépare le renoncement public du bûcher (Ac 19:19).",
        en: "\"Many also of those who had believed came, confessing and declaring their deeds.\" (Acts 19:18) Light begins with truth about oneself; confession precedes and prepares the public renunciation of the bonfire (Acts 19:19).",
      },
    },
    {
      id: "qA3",
      level: "A",
      prompt: { fr: "Dans le théâtre en émeute, que savait la plus grande partie de la foule ?", en: "In the rioting theater, what did most of the crowd know?" },
      choices: [
        { fr: "Tous connaissaient précisément l'accusation portée contre Paul", en: "They all knew exactly the charge brought against Paul" },
        { fr: "Chacun avait vu Paul parler contre la déesse", en: "Each one had seen Paul speak against the goddess" },
        { fr: "La plupart ne savaient même pas pourquoi ils étaient assemblés", en: "Most did not even know why they had come together" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Les uns criaient d'une manière, les autres d'une autre; car le désordre régnait dans l'assemblée, et la plupart ne savaient pas pourquoi ils s'étaient réunis. » (Actes 19:32) Portrait saisissant d'une foule : beaucoup de bruit, peu de raisons.",
        en: "\"Some therefore cried one thing, and some another, for the assembly was in confusion. Most of them didn't know why they had come together.\" (Acts 19:32) A striking portrait of a mob: much noise, few reasons.",
      },
    },
    {
      id: "qA4",
      level: "A",
      prompt: { fr: "Qui étaient Priscille et Aquilas, présents à Éphèse ?", en: "Who were Priscilla and Aquila, present in Ephesus?" },
      choices: [
        { fr: "Des orfèvres associés de Démétrius", en: "Silversmiths in partnership with Demetrius" },
        { fr: "Des collaborateurs de Paul qui exposèrent plus exactement la voie de Dieu à Apollos", en: "Fellow workers of Paul who explained the way of God more accurately to Apollos" },
        { fr: "Des magistrats de la ville", en: "Magistrates of the city" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Faiseurs de tentes comme Paul (Ac 18:3), Aquilas et Priscille l'accompagnèrent jusqu'à Éphèse (Ac 18:18-19). Quand Apollos y prêcha, « ils le prirent avec eux, et lui exposèrent plus exactement la voie de Dieu » (Actes 18:26) : un ministère discret et décisif.",
        en: "Tentmakers like Paul (Acts 18:3), Aquila and Priscilla went with him as far as Ephesus (Acts 18:18-19). When Apollos preached there, \"they took him aside, and explained to him the way of God more accurately\" (Acts 18:26): a quiet, decisive ministry.",
      },
    },
    {
      id: "qE1",
      level: "E",
      prompt: { fr: "Quel argument le greffier avança-t-il en faveur de Gaïus et d'Aristarque ?", en: "What argument did the town clerk put forward in favor of Gaius and Aristarchus?" },
      choices: [
        { fr: "« Ces hommes ne sont coupables ni de sacrilège, ni de blasphème envers notre déesse »", en: "\"These men are neither robbers of temples nor blasphemers of your goddess\"" },
        { fr: "« Ces hommes sont sous la protection de Rome »", en: "\"These men are under the protection of Rome\"" },
        { fr: "« La déesse elle-même les a déclarés innocents »", en: "\"The goddess herself has declared them innocent\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Vous avez amené ces hommes, qui ne sont coupables ni de sacrilège, ni de blasphème envers notre déesse. » (Actes 19:37) Témoignage remarquable : après deux ans de prédication, même leurs adversaires ne purent citer une seule insulte de Paul contre Artémis. La Parole avait changé les cœurs sans mépriser les personnes.",
        en: "\"You have brought these men here, who are neither robbers of temples nor blasphemers of your goddess.\" (Acts 19:37) A remarkable testimony: after two years of preaching, even their opponents could not cite one insult from Paul against Artemis. The Word had changed hearts without despising people.",
      },
    },
    {
      id: "qE2",
      level: "E",
      prompt: { fr: "Combien de temps la foule cria-t-elle d'une seule voix dans le théâtre ?", en: "For how long did the crowd cry out with one voice in the theater?" },
      choices: [
        { fr: "Quelques minutes seulement", en: "Only a few minutes" },
        { fr: "Environ deux heures", en: "About two hours" },
        { fr: "Du matin jusqu'au soir", en: "From morning until evening" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Tous crièrent d'une seule voix pendant près de deux heures: Grande est la Diane des Éphésiens ! » (Actes 19:34) Deux heures d'un même cri — face à quoi la Parole du Seigneur, elle, « croissait en puissance et en force » (Ac 19:20) sans avoir besoin de crier.",
        en: "\"All with one voice for a time of about two hours cried out, 'Great is Diana of the Ephesians!'\" (Acts 19:34) Two hours of one chant — while the Word of the Lord \"grew and became mighty\" (Acts 19:20) with no need to shout.",
      },
    },
    {
      id: "qE3",
      level: "E",
      prompt: { fr: "De quel danger le greffier avertit-il l'assemblée avant de la congédier ?", en: "Of what danger did the town clerk warn the assembly before dismissing it?" },
      choices: [
        { fr: "Que la déesse punirait la ville", en: "That the goddess would punish the city" },
        { fr: "Que Paul reviendrait avec des soldats", en: "That Paul would come back with soldiers" },
        { fr: "Que la ville risquait d'être accusée de sédition pour ce rassemblement sans motif", en: "That the city risked being charged with rioting for this gathering without cause" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Nous risquons, en effet, d'être accusés de sédition pour ce qui s'est passé aujourd'hui, puisqu'il n'existe aucun motif qui nous permette de justifier cet attroupement. » (Actes 19:40) Rome ne tolérait pas les émeutes : la sagesse du greffier dispersa la foule — et l'Évangile poursuivit sa course (Ac 20:1).",
        en: "\"For indeed we are in danger of being accused concerning today's riot, there being no cause. Concerning it, we wouldn't be able to give an account of this commotion.\" (Acts 19:40) Rome did not tolerate riots: the clerk's wisdom dispersed the crowd — and the Gospel went on its way (Acts 20:1).",
      },
    },
    {
      id: "qD5",
      level: "D",
      prompt: { fr: "Quel baptême les douze disciples rencontrés par Paul à Éphèse connaissaient-ils seulement ?", en: "What baptism did the twelve disciples Paul met at Ephesus know only?" },
      choices: [
        { fr: "Le baptême de la mer Rouge", en: "The baptism of the Red Sea" },
        { fr: "Le baptême de Jean", en: "John's baptism" },
        { fr: "Le baptême du feu", en: "The baptism of fire" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« De quel baptême avez-vous donc été baptisés ? Et ils répondirent: Du baptême de Jean. » (Actes 19:3) Paul leur annonça Jésus, ils furent baptisés en son nom, et le Saint-Esprit vint sur eux (Ac 19:4-6) : le baptême de repentance trouvait son accomplissement.",
        en: "\"'Into what then were you baptized?' They said, 'Into John's baptism.'\" (Acts 19:3) Paul told them of Jesus; they were baptized in his name, and the Holy Spirit came on them (Acts 19:4-6): the baptism of repentance found its fulfillment.",
      },
    },
    {
      id: "qD6",
      level: "D",
      prompt: { fr: "Qui était Apollos, venu à Éphèse avant ce séjour de Paul ?", en: "Who was Apollos, who came to Ephesus before this stay of Paul?" },
      choices: [
        { fr: "Un Juif d'Alexandrie, homme éloquent et versé dans les Écritures", en: "A Jew of Alexandria, an eloquent man, mighty in the Scriptures" },
        { fr: "Un orfèvre associé de Démétrius", en: "A silversmith in partnership with Demetrius" },
        { fr: "Un magistrat de la ville", en: "A magistrate of the city" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Un Juif nommé Apollos, originaire d'Alexandrie, homme éloquent et versé dans les Écritures, arriva à Éphèse. » (Actes 18:24) Fervent mais ne connaissant que le baptême de Jean, il fut instruit plus exactement par Priscille et Aquilas (Ac 18:25-26), puis devint un puissant défenseur de la foi (Ac 18:27-28).",
        en: "\"Now a certain Jew named Apollos, an Alexandrian by race, an eloquent man, came to Ephesus. He was mighty in the Scriptures.\" (Acts 18:24) Fervent yet knowing only John's baptism, he was taught more accurately by Priscilla and Aquila (Acts 18:25-26), then became a powerful defender of the faith (Acts 18:27-28).",
      },
    },
    {
      id: "qD7",
      level: "D",
      prompt: { fr: "Par quels objets Dieu opérait-il des miracles extraordinaires par les mains de Paul ?", en: "Through what objects did God work extraordinary miracles by Paul's hands?" },
      choices: [
        { fr: "Par des pièces d'argent bénies", en: "Through blessed silver coins" },
        { fr: "Par l'eau du port d'Éphèse", en: "Through the water of the harbor of Ephesus" },
        { fr: "Par des linges et des mouchoirs qui avaient touché son corps", en: "Through handkerchiefs and aprons that had touched his body" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Dieu faisait des miracles extraordinaires par les mains de Paul, au point qu'on appliquait sur les malades des linges ou des mouchoirs qui avaient touché son corps, et les maladies les quittaient. » (Actes 19:11-12) Dans la capitale de la magie, Dieu montra une puissance qui ne devait rien aux formules secrètes.",
        en: "\"God worked special miracles by the hands of Paul, so that even handkerchiefs or aprons were carried away from his body to the sick, and the diseases departed from them.\" (Acts 19:11-12) In the capital of magic, God displayed a power that owed nothing to secret formulas.",
      },
    },
    {
      id: "qA5",
      level: "A",
      prompt: { fr: "Que répondit l'esprit mauvais aux sept fils de Scéva qui invoquaient le nom de Jésus ?", en: "What did the evil spirit answer the seven sons of Sceva who invoked the name of Jesus?" },
      choices: [
        { fr: "« Je connais Jésus, et je sais qui est Paul; mais vous, qui êtes-vous ? »", en: "\"Jesus I know, and Paul I know, but who are you?\"" },
        { fr: "« Sortez de cette maison, hommes de Dieu »", en: "\"Leave this house, men of God\"" },
        { fr: "« Votre autorité est plus grande que celle de Paul »", en: "\"Your authority is greater than Paul's\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« L'esprit malin leur répondit: Je connais Jésus, et je sais qui est Paul; mais vous, qui êtes-vous ? » (Actes 19:15) Le nom de Jésus n'est pas une formule magique : sans relation vivante avec lui, l'invocation est vide — et les exorcistes s'enfuirent battus (Ac 19:16).",
        en: "\"The evil spirit answered, 'Jesus I know, and Paul I know, but who are you?'\" (Acts 19:15) The name of Jesus is not a magic formula: without a living relationship with him, the invocation is empty — and the exorcists fled, beaten (Acts 19:16).",
      },
    },
    {
      id: "qA6",
      level: "A",
      prompt: { fr: "Quel fut l'effet de la mésaventure des fils de Scéva sur la ville d'Éphèse ?", en: "What effect did the misadventure of the sons of Sceva have on the city of Ephesus?" },
      choices: [
        { fr: "On chassa tous les exorcistes de la ville", en: "All the exorcists were driven out of the city" },
        { fr: "La crainte saisit tous les habitants, et le nom du Seigneur Jésus était glorifié", en: "Fear fell on all the inhabitants, and the name of the Lord Jesus was magnified" },
        { fr: "Le récit fut vite oublié", en: "The story was quickly forgotten" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Cela fut connu de tous les Juifs et de tous les Grecs qui demeuraient à Éphèse, et la crainte s'empara d'eux tous, et le nom du Seigneur Jésus était glorifié. » (Actes 19:17) C'est ce saisissement qui prépara les confessions publiques et le bûcher des livres de magie (Ac 19:18-19).",
        en: "\"This became known to all, both Jews and Greeks, who lived at Ephesus. Fear fell on them all, and the name of the Lord Jesus was magnified.\" (Acts 19:17) That awe prepared the public confessions and the burning of the magic books (Acts 19:18-19).",
      },
    },
    {
      id: "qA7",
      level: "A",
      prompt: { fr: "Selon le greffier, de quoi la ville d'Éphèse était-elle la « gardienne » ?", en: "According to the town clerk, of what was the city of Ephesus the 'temple keeper'?" },
      choices: [
        { fr: "Du trésor impérial de l'Asie", en: "Of the imperial treasury of Asia" },
        { fr: "Des archives de la province", en: "Of the archives of the province" },
        { fr: "Du temple de la grande Diane", en: "Of the temple of the great Diana" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Hommes Éphésiens, quel est celui qui ignore que la ville d'Éphèse est la gardienne du temple de la grande Diane… ? » (Actes 19:35) Le greffier partit de cette fierté incontestée pour montrer que rien ne justifiait l'émeute (Ac 19:36-40) — et la sagesse dispersa la foule.",
        en: "\"You men of Ephesus, what man is there who doesn't know that the city of the Ephesians is temple keeper of the great goddess Diana…?\" (Acts 19:35) The clerk started from that undisputed pride to show that nothing justified the riot (Acts 19:36-40) — and wisdom dispersed the crowd.",
      },
    },
    {
      id: "qE4",
      level: "E",
      prompt: { fr: "Quel projet Paul forma-t-il pendant ce séjour à Éphèse ?", en: "What plan did Paul form during this stay in Ephesus?" },
      choices: [
        { fr: "Retourner aussitôt à Antioche par la mer", en: "Return at once to Antioch by sea" },
        { fr: "Traverser la Macédoine et l'Achaïe, aller à Jérusalem, puis voir Rome", en: "Pass through Macedonia and Achaia, go to Jerusalem, then see Rome" },
        { fr: "S'établir définitivement à Éphèse", en: "Settle permanently in Ephesus" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Paul forma le projet d'aller à Jérusalem, en traversant la Macédoine et l'Achaïe. Quand j'aurai été là, dit-il, il faut aussi que je voie Rome. » (Actes 19:21) Ce simple projet de voyage porte tout le reste du livre des Actes : Jérusalem, la captivité, la tempête — et Rome (Ac 28:14-16).",
        en: "\"Paul determined in the Spirit, when he had passed through Macedonia and Achaia, to go to Jerusalem, saying, 'After I have been there, I must also see Rome.'\" (Acts 19:21) That simple travel plan carries the whole rest of Acts: Jerusalem, captivity, the storm — and Rome (Acts 28:14-16).",
      },
    },
    {
      id: "qE5",
      level: "E",
      prompt: { fr: "Quels aides Paul envoya-t-il en Macédoine avant l'émeute ?", en: "Which helpers did Paul send into Macedonia before the riot?" },
      choices: [
        { fr: "Timothée et Éraste", en: "Timothy and Erastus" },
        { fr: "Gaïus et Aristarque", en: "Gaius and Aristarchus" },
        { fr: "Priscille et Aquilas", en: "Priscilla and Aquila" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Il envoya en Macédoine deux de ses aides, Timothée et Éraste, et il resta lui-même quelque temps encore en Asie. » (Actes 19:22) Paul travaillait en équipe : pendant qu'il tenait Éphèse, ses collaborateurs préparaient l'étape suivante du voyage.",
        en: "\"Having sent into Macedonia two of those who served him, Timothy and Erastus, he himself stayed in Asia for a while.\" (Acts 19:22) Paul worked as a team: while he held Ephesus, his co-workers prepared the journey's next stage.",
      },
    },
    {
      id: "qE6",
      level: "E",
      prompt: { fr: "Qui pria Paul de ne pas se rendre lui-même dans le théâtre en émeute ?", en: "Who begged Paul not to venture himself into the rioting theater?" },
      choices: [
        { fr: "Démétrius et les orfèvres", en: "Demetrius and the silversmiths" },
        { fr: "Le greffier de la ville", en: "The town clerk" },
        { fr: "Des Asiarques, qui étaient ses amis, avec les disciples", en: "Some of the Asiarchs, who were his friends, along with the disciples" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Paul voulait se présenter devant le peuple, mais les disciples l'en empêchèrent; quelques-uns même des Asiarques, qui étaient ses amis, envoyèrent vers lui, pour l'engager à ne pas se rendre au théâtre. » (Actes 19:30-31) Même de hauts dignitaires de l'Asie protégeaient Paul : deux ans de fidélité lui avaient gagné des amis jusque parmi les autorités.",
        en: "\"When Paul wanted to enter in to the people, the disciples didn't allow him. Certain also of the Asiarchs, being his friends, sent to him and begged him not to venture into the theater.\" (Acts 19:30-31) Even high officials of Asia protected Paul: two years of faithfulness had won him friends among the authorities themselves.",
      },
    },
    {
      id: "qD8",
      level: "D",
      prompt: { fr: "Combien de fils de Scéva tentaient de chasser les mauvais esprits par le nom de Jésus ?", en: "How many sons of Sceva were trying to cast out evil spirits by the name of Jesus?" },
      choices: [
        { fr: "Trois", en: "Three" },
        { fr: "Douze", en: "Twelve" },
        { fr: "Sept", en: "Seven" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Ceux qui faisaient cela étaient sept fils de Scéva, Juif, l'un des principaux sacrificateurs. » (Actes 19:14) Ils employaient le nom de Jésus comme une formule — « Je vous conjure par Jésus que Paul prêche » (Ac 19:13) — sans le connaître eux-mêmes : le nom du Seigneur n'est pas un talisman, mais l'appel d'une relation vivante.",
        en: "\"There were seven sons of one Sceva, a Jewish chief priest, who did this.\" (Acts 19:14) They used the name of Jesus as a formula — \"I adjure you by Jesus whom Paul preaches\" (Acts 19:13) — without knowing him themselves: the Lord's name is not a talisman, but the call of a living relationship.",
      },
    },
    {
      id: "qE7",
      level: "E",
      prompt: { fr: "Quelle parole du Seigneur Jésus Paul rappela-t-il plus tard aux anciens d'Éphèse, réunis à Milet ?", en: "What saying of the Lord Jesus did Paul later recall to the Ephesian elders gathered at Miletus?" },
      choices: [
        { fr: "« Il y a plus de bonheur à donner qu'à recevoir »", en: "\"It is more blessed to give than to receive\"" },
        { fr: "« Rendez à César ce qui est à César »", en: "\"Give to Caesar what is Caesar's\"" },
        { fr: "« Le sabbat a été fait pour l'homme »", en: "\"The Sabbath was made for man\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Il faut… se rappeler les paroles du Seigneur Jésus, qui a dit lui-même: Il y a plus de bonheur à donner qu'à recevoir. » (Actes 20:35) À Milet, Paul rappela aussi qu'à Éphèse, durant trois années, il n'avait cessé « nuit et jour d'exhorter avec larmes chacun » (Ac 20:31) — le fruit durable du ministère commencé à l'école de Tyrannus.",
        en: "\"You should remember the words of the Lord Jesus, that he himself said, 'It is more blessed to give than to receive.'\" (Acts 20:35) At Miletus, Paul also recalled that at Ephesus, for three years, he \"didn't cease to admonish everyone night and day with tears\" (Acts 20:31) — the lasting fruit of the ministry begun in the school of Tyrannus.",
      },
    },
  ],
  tips: [
    {
      fr: "Commence à l'ouest : dresse le pupitre de l'école de Tyrannus, apporte-lui les trois rouleaux des Écritures et tiens l'enseignement quotidien (Ac 19:9-10).",
      en: "Start in the west: set up the lectern of the school of Tyrannus, bring it the three Scripture scrolls, and hold the daily teaching (Acts 19:9-10).",
    },
    {
      fr: "Rassemble les cinq livres de magie dispersés dans la ville et porte-les un à un au bûcher — puis ramasse les pièces du compte des cinquante mille (Ac 19:19).",
      en: "Gather the five magic books scattered through the city and carry them one by one to the bonfire — then pick up the coins of the fifty-thousand count (Acts 19:19).",
    },
    {
      fr: "Pour traverser l'émeute du théâtre, ne réponds pas aux cris : conduis Gaïus puis Aristarque vers la porte sud, et maintiens le greffier jusqu'à ce que la foule s'apaise (Ac 19:29-41).",
      en: "To cross the theater riot, do not answer the shouts: lead Gaius then Aristarchus to the south gate, and hold the town clerk until the crowd grows calm (Acts 19:29-41).",
    },
  ],
  lesson: {
    fr: "C'est ainsi que la parole du Seigneur croissait en puissance et en force. (Actes 19:20)",
    en: "So the word of the Lord grew and became mighty. (Acts 19:20)",
  },
  audio: { ambience: "temple" },
  multiplayer: { mode: "coop", maxPlayers: 4 },
};

export default voyagesDePaulScene3;
